import { useQuery } from '@tanstack/react-query'
import { EndpointHint, Skeleton } from '../../core/index.js'
import type { MessagesAdminApi } from '../adminApi.js'
import { CHANNEL_NOUN } from '../delivery/deliveryText.js'
import type { ChannelTotals } from '../types.js'
import { LoadFailure } from '../../core/index.js'

function TotalsTable({ title, totals, channels }: { title: string; totals: Record<string, ChannelTotals>; channels: readonly string[] }) {
  return (
    <div>
      <div className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-ink-mid">{title}</div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-ink-mid">
            <th className="py-1 pr-3 font-bold">Channel</th>
            <th className="py-1 pr-3 font-bold">Sent</th>
            <th className="py-1 pr-3 font-bold">Failed</th>
            <th className="py-1 pr-3 font-bold">Addresses gone</th>
            <th className="py-1 font-bold">Text parts</th>
          </tr>
        </thead>
        <tbody>
          {channels.map((channel) => {
            const row = totals[channel] ?? { sent: 0, failed: 0, addressGone: 0, segments: 0 }
            return (
              <tr key={channel} className="border-t border-marine/10">
                <td className="py-1.5 pr-3 font-bold text-ink">{CHANNEL_NOUN[channel] ?? channel}</td>
                <td className="py-1.5 pr-3 text-green">{row.sent}</td>
                <td className={`py-1.5 pr-3 ${row.failed > 0 ? 'text-coral' : 'text-ink-mid'}`}>{row.failed}</td>
                <td className={`py-1.5 pr-3 ${row.addressGone > 0 ? 'text-coral' : 'text-ink-mid'}`}>{row.addressGone}</td>
                <td className="py-1.5 text-ink-mid">{channel === 'sms' ? (row.segments ?? 0) : '–'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * System › Delivery (Clinical Bridge plan section 13.6): which channels
 * this deployment can send on, and how delivery has gone by channel today
 * and over the last seven days. Counters for the whole deployment, from
 * the receipts' day items, not a client's data.
 */
export function DeliveryPage({ api }: { api: Pick<MessagesAdminApi, 'deliveryOverview'> }) {
  const overview = useQuery({ queryKey: ['admin', 'delivery-overview'], queryFn: () => api.deliveryOverview() })
  if (overview.isPending) {
    return <Skeleton />
  }
  if (overview.isError) {
    return <LoadFailure size="xs" message="The delivery overview could not be loaded." onRetry={() => void overview.refetch()} retrying={overview.isFetching} />
  }
  const { channels, today, lastSevenDays, days, byClient = [] } = overview.data
  const seen = new Set<string>([...channels.map((channel) => channel.key), ...Object.keys(lastSevenDays)])
  const keys = [...seen]
  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-lg border border-marine/15 bg-white p-5">
        <div className="mb-3 flex flex-wrap items-baseline gap-x-3 text-[10px] font-bold uppercase tracking-wide text-marine">
          Channels on this deployment
          <EndpointHint>GET /admin/delivery</EndpointHint>
        </div>
        {channels.length === 0 ? (
          <p className="text-xs text-ink-mid">None. Every message stays in the app until a channel's parameters are set on the stack (VAPID keys for push, a verified SES sender for email, a registered number for SMS).</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {channels.map((channel) => (
              <li key={channel.key} className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-bold text-ink">{channel.label}</span>
                <span className="font-mono text-[11px] text-ink-mid">{channel.key}</span>
                <span className="text-ink-mid">· {channel.verification === 'code' ? 'address proves itself with a code' : 'good on registration'}</span>
                {channel.publicKey && <span className="break-all font-mono text-[10px] text-ink-mid/70">· public key {channel.publicKey.slice(0, 12)}…</span>}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-[11px] text-ink-mid">Which messages leave the app is a policy per notification type: Clinical Bridge alerts and staff replies go out at once; everything else stays on the worker's Messages tab. SMS stays off until the sending number is registered (plan D17).</p>
      </section>
      <section className="grid gap-6 rounded-lg border border-marine/15 bg-white p-5 md:grid-cols-2">
        <TotalsTable title="Today (UTC)" totals={today} channels={keys} />
        <TotalsTable title="Last seven days" totals={lastSevenDays} channels={keys} />
      </section>
      <section className="rounded-lg border border-marine/15 bg-white p-5">
        <div className="mb-1.5 flex flex-wrap items-baseline gap-x-3 text-[9px] font-bold uppercase tracking-wide text-ink-mid">
          Texts by client
          <EndpointHint>GET /admin/clients/{'{clientId}'}/delivery</EndpointHint>
        </div>
        {byClient.length === 0 ? (
          <p className="text-xs text-ink-mid">No client has been sent anything in the last seven days. Texts are metered per client by sends and billable parts (SMS plan 3.8); the client's detail shows its month.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-ink-mid">
                <th className="py-1 pr-3 font-bold">Client</th>
                <th className="py-1 pr-3 font-bold">Texts today</th>
                <th className="py-1 pr-3 font-bold">Parts today</th>
                <th className="py-1 pr-3 font-bold">Texts, 7 days</th>
                <th className="py-1 font-bold">Parts, 7 days</th>
              </tr>
            </thead>
            <tbody>
              {byClient.map((client) => (
                <tr key={client.clientId} className="border-t border-marine/10">
                  <td className="py-1.5 pr-3 font-bold text-ink">
                    {client.organizationName}
                  </td>
                  <td className="py-1.5 pr-3 text-ink">{client.today['sms']?.sent ?? 0}</td>
                  <td className="py-1.5 pr-3 text-ink-mid">{client.today['sms']?.segments ?? 0}</td>
                  <td className="py-1.5 pr-3 text-ink">{client.lastSevenDays['sms']?.sent ?? 0}</td>
                  <td className="py-1.5 text-ink-mid">{client.lastSevenDays['sms']?.segments ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <section className="rounded-lg border border-marine/15 bg-white p-5">
        <div className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-ink-mid">By day</div>
        {days.length === 0 ? (
          <p className="text-xs text-ink-mid">Nothing has been delivered in the last seven days.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {days.map((day) => (
              <li key={day.day} className="flex flex-wrap gap-x-3">
                <span className="font-mono text-ink">{day.day}</span>
                {Object.entries(day.byChannel).map(([channel, counts]) => (
                  <span key={channel} className="text-ink-mid">
                    {CHANNEL_NOUN[channel] ?? channel}: {counts.sent} sent{counts.failed > 0 ? `, ${counts.failed} failed` : ''}{counts.addressGone > 0 ? `, ${counts.addressGone} gone` : ''}
                  </span>
                ))}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
